import { z } from 'zod';
import { IdZ, MediaHashZ, TsZ } from './common';

/**
 * Frozen media and studio entity schemas (docs/design/add-fullbody.md "Foundation additions").
 * Files are content addressed and served at /media/:hash.:ext (R15), gated by the session cookie.
 */

export const MEDIA_EXTS = ['jpg', 'png', 'webp'] as const;
export const MediaExtZ = z.enum(MEDIA_EXTS);
export const MEDIA_KINDS = [
  'photo_body',
  'photo_face',
  'photo_thumb',
  'art',
  'cutout',
  'sticker',
  'face',
  'thumb',
] as const;
export const MediaKindZ = z.enum(MEDIA_KINDS);
export type MediaKind = z.infer<typeof MediaKindZ>;

export const MediaFileZ = z.object({
  hash: MediaHashZ,
  ext: MediaExtZ,
  mime: z.string(),
  bytes: z.number().int().nonnegative(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  kind: MediaKindZ,
  ownerPlayerId: IdZ,
  createdAt: TsZ,
});
export type MediaFile = z.infer<typeof MediaFileZ>;

/** Public path of a media file. Variants are separate hashes. */
export function mediaUrl(hash: string, ext: (typeof MEDIA_EXTS)[number]): string {
  return `/media/${hash}.${ext}`;
}

const Box = z.tuple([z.number(), z.number(), z.number(), z.number()]);

export const PortraitLayoutZ = z.object({
  v: z.literal(1),
  /** Figure bounding box [x, y, w, h], normalized 0..1 of the cutout. */
  bbox: Box,
  /** Head circle [cx, cy, r], normalized 0..1 of the cutout (r is a share of the cutout height). */
  head: z.tuple([z.number(), z.number(), z.number()]),
  touchesEdge: z.boolean(),
  /** Figure box inside the sticker PNG [x, y, w, h]. */
  stickerBox: Box,
  stickerW: z.number().int().positive(),
  stickerH: z.number().int().positive(),
});
export type PortraitLayout = z.infer<typeof PortraitLayoutZ>;

export const PortraitNudgeZ = z.object({
  dy: z.number().min(-20).max(20),
  scale: z.number().min(0.9).max(1.1),
});
export type PortraitNudge = z.infer<typeof PortraitNudgeZ>;

export const STUDIO_PRESETS = ['rookie_card', 'streetball', 'game_cover'] as const;
export const StudioPresetZ = z.enum(STUDIO_PRESETS);
export type StudioPreset = z.infer<typeof StudioPresetZ>;
export const PortraitLookZ = z.enum(['toon', 'sticker']);
export const PortraitClothingZ = z.enum(['keep', 'jersey']);
export const PortraitPoseZ = z.enum(['hero', 'mine']);
export const FunnyLevelZ = z.number().int().min(0).max(3);

/** What the card and avatars need. Carried by PlayerDetail and PlayerListItem as `portrait`. */
export const PortraitArtRefZ = z.object({
  portraitId: IdZ,
  preset: StudioPresetZ,
  look: PortraitLookZ,
  stickerUrl: z.string().nullable(),
  faceUrl: z.string().nullable(),
  thumbUrl: z.string().nullable(),
  artUrl: z.string(),
  cutoutUrl: z.string().nullable(),
  layout: PortraitLayoutZ.nullable(),
  nudge: PortraitNudgeZ.nullable(),
});
export type PortraitArtRef = z.infer<typeof PortraitArtRefZ>;

export const StudioPhotoKindZ = z.enum(['body', 'face']);
export const StudioPhotoZ = z.object({
  id: IdZ,
  playerId: IdZ,
  uploadedBy: IdZ,
  kind: StudioPhotoKindZ,
  url: z.string(),
  thumbUrl: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  /** [x, y, w, h] in 0..1, null for a face photo. */
  bodyBox: Box.nullable(),
  checks: z.array(z.string()),
  createdAt: TsZ,
});
export type StudioPhoto = z.infer<typeof StudioPhotoZ>;

export const PORTRAIT_JOB_STATUSES = ['queued', 'editing', 'cutting', 'done', 'failed', 'canceled'] as const;
export const PortraitJobStatusZ = z.enum(PORTRAIT_JOB_STATUSES);
export const PortraitErrorCodeZ = z.enum([
  'blocked',
  'invalid',
  'auth',
  'exhausted',
  'timeout',
  'cutout_failed',
  'canceled',
]);

export const PortraitJobZ = z.object({
  id: IdZ,
  playerId: IdZ,
  requestedBy: IdZ,
  preset: StudioPresetZ,
  funnyLevel: FunnyLevelZ,
  clothing: PortraitClothingZ,
  pose: PortraitPoseZ,
  look: PortraitLookZ,
  bodyPhotoId: IdZ,
  facePhotoId: IdZ.nullable(),
  parentJobId: IdZ.nullable(),
  provider: z.string(),
  model: z.string(),
  status: PortraitJobStatusZ,
  attempts: z.number().int().nonnegative(),
  /** 1-based place in the queue while queued, else null. */
  queuePos: z.number().int().positive().nullable(),
  estCostUsd: z.number().nonnegative(),
  costUsd: z.number().nonnegative(),
  errorCode: PortraitErrorCodeZ.nullable(),
  errorMessage: z.string().nullable(),
  portraitId: IdZ.nullable(),
  queuedAt: TsZ,
  startedAt: TsZ.nullable(),
  finishedAt: TsZ.nullable(),
});
export type PortraitJob = z.infer<typeof PortraitJobZ>;

export const PortraitZ = z.object({
  id: IdZ,
  jobId: IdZ,
  playerId: IdZ,
  preset: StudioPresetZ,
  look: PortraitLookZ,
  artUrl: z.string(),
  cutoutUrl: z.string().nullable(),
  stickerUrl: z.string().nullable(),
  faceUrl: z.string().nullable(),
  thumbUrl: z.string().nullable(),
  layout: PortraitLayoutZ.nullable(),
  nudge: PortraitNudgeZ.nullable(),
  cutoutFailed: z.boolean(),
  pinned: z.boolean(),
  hidden: z.boolean(),
  active: z.boolean(),
  createdAt: TsZ,
});
export type Portrait = z.infer<typeof PortraitZ>;
